import '../../../../node_modules/bootstrap/js/src/tab';

// =====================================================================
//  Pre-screening engine
//  Checks a lender's rules against user profile + loan amount.
//  Returns { eligible, reasons: [{code, params}] } — the engine emits
//  structured reason codes (no user-facing strings) so the UI layer can
//  render them in any language using templates from HTML.
//
//  Use Calc.eligibility.renderReason(reason) to format one reason against
//  the templates defined on `body[data-elig-tpl-*]` attributes, or pass
//  your own template map.
// =====================================================================
window.Calc = window.Calc || {};
Calc.eligibility = {

  // Return the maxDsr applicable to a given income, by walking the tiers.
  resolveDsrTier(dsrTiers, income) {
    if (!dsrTiers || !income) return null;
    for (const tier of dsrTiers) {
      if (income <= tier.incomeUpTo) return tier.maxDsr;
    }
    return null;
  },

  check(bank, profile, loanAmount, currentMonthlyCommitment = 0) {
    const e = bank.eligibility || {};
    const reasons = [];

    // 1. Age
    if (profile.age) {
      if (e.minAge && profile.age < e.minAge) reasons.push({ code: 'min_age', params: { min: e.minAge } });
      if (e.maxAge && profile.age > e.maxAge) reasons.push({ code: 'max_age', params: { max: e.maxAge } });
    }

    // 2. Min income (gross)
    if (profile.income && e.minIncome && profile.income < e.minIncome) {
      reasons.push({ code: 'min_income', params: { min: e.minIncome } });
    }

    // 3. Employment type
    // Filter to non-empty entries first: upstream feeds sometimes deliver
    // [null], [""], or whitespace-only strings to mean "no constraint", but
    // those still satisfy a naive truthy/length check and would render as
    // "(accepts: )" — ugly and misleading. Treat as "constraint absent".
    const validEmpTypes = Array.isArray(e.employmentTypes)
      ? e.employmentTypes.filter(t => t != null && String(t).trim() !== '')
      : [];
    if (profile.incomeType && validEmpTypes.length &&
        !validEmpTypes.includes(profile.incomeType)) {
      reasons.push({ code: 'employment_not_accepted', params: { accepts: validEmpTypes.join(', ') } });
    }

    // 4. Loan amount range — each bound is opt-in: 0, "0", null, "" from the
    // feed all mean "no constraint on this side", so we skip rather than
    // treating it as a hard limit (which would mean "max loan RM0" — wrong).
    // Numeric coercion is required because upstream sometimes ships strings.
    if (loanAmount) {
      const minA = Number(bank.minAmount);
      const maxA = Number(bank.maxAmount);
      if (minA > 0 && loanAmount < minA) {
        reasons.push({ code: 'min_loan', params: { min: minA } });
      }
      if (maxA > 0 && loanAmount > maxA) {
        reasons.push({ code: 'max_loan', params: { max: maxA } });
      }
    }

    // 5. DSR tier check — skip entirely when the feed delivers an empty
    // tier list (i.e. "constraint absent"). Without this guard, resolveDsrTier
    // falls through to null and incorrectly fires "no_lend_tier".
    if (profile.income && loanAmount && Array.isArray(e.dsrTiers) && e.dsrTiers.length) {
      const maxDsr = Calc.eligibility.resolveDsrTier(e.dsrTiers, profile.income);
      if (maxDsr === null || maxDsr === undefined) {
        reasons.push({ code: 'no_lend_tier', params: {} });
      } else {
        const newInstalment = (bank.rateType === 'flat'
          ? Calc.finance.flatRate(loanAmount, bank.interestRate / 100, bank.maxTenure)
          : Calc.finance.reducingRate(loanAmount, bank.interestRate / 100, bank.maxTenure)
        ).monthlyInstalment;

        const dsr = Calc.finance.dsr(currentMonthlyCommitment + newInstalment, profile.income);
        if (dsr > maxDsr) {
          reasons.push({ code: 'dsr_exceeded', params: { dsr: (dsr * 100).toFixed(1), max: (maxDsr * 100).toFixed(0) } });
        }
      }
    }

    return {
      eligible: reasons.length === 0,
      reasons,
      // Useful for UI: which DSR tier applied
      appliedMaxDsr: profile.income
        ? Calc.eligibility.resolveDsrTier(e.dsrTiers, profile.income)
        : null,
    };
  },

  filterEligible(banks, profile, loanAmount, currentMonthlyCommitment = 0) {
    return banks.filter(b =>
      Calc.eligibility.check(b, profile, loanAmount, currentMonthlyCommitment).eligible
    );
  },

  // -------------------------------------------------------------------
  //  Reason rendering — pulls translatable templates from <body> data-*
  //  attributes (e.g. data-elig-tpl-min-age="Min age {min}").
  //  Substitutes {param} placeholders. JS holds zero user-facing strings.
  // -------------------------------------------------------------------
  renderReason(reason, templates) {
    if (reason == null) return '';
    // Back-compat: legacy callers (or upstream feeds) sometimes hand us a
    // plain string instead of a {code, params} object. Pass it through so
    // the UI shows real content instead of an empty colon.
    if (typeof reason === 'string') return reason;
    const map = templates || Calc.eligibility._templatesFromDOM();
    const tpl = map && map[reason.code];
    // No template? Emit the raw code rather than an empty string — that way
    // an unmapped code shows up as a visible diagnostic ("RHB: some_new_code")
    // instead of silently disappearing. All real templates live in the HTML
    // layer (see partials/elig-templates.php); JS holds zero user-facing strings.
    if (!tpl) return reason.code || '(unknown reason)';
    return tpl.replace(/\{(\w+)\}/g, (_, k) => {
      const v = reason.params && reason.params[k];
      if (v == null) return '';
      // Format big numbers as locale-grouped (e.g. 15000 -> "15,000").
      return typeof v === 'number' ? v.toLocaleString() : String(v);
    });
  },
  renderReasons(reasons, templates) {
    if (!Array.isArray(reasons)) return [];
    const map = templates || Calc.eligibility._templatesFromDOM();
    return reasons.map(r => Calc.eligibility.renderReason(r, map));
  },
  _templatesFromDOM() {
    if (Calc.eligibility._tplCache) return Calc.eligibility._tplCache;
    const out = {};
    const ds = document.body ? document.body.dataset : {};
    // data-elig-tpl-min-age → keyed as 'min_age'
    Object.keys(ds).forEach(k => {
      const m = k.match(/^eligTpl(.+)$/);
      if (m) {
        const code = m[1].replace(/[A-Z]/g, c => '_' + c.toLowerCase()).replace(/^_/, '');
        out[code] = ds[k];
      }
    });
    Calc.eligibility._tplCache = out;
    return out;
  },
};
